import type { RegionKey } from "./sql";
import type { FxRow } from "../../lib/queries/useFxRates";

export type FxArrMap = Record<RegionKey, number[]>;

export const FX_BASE = "USD";
export const FX_QUOTES = ["NZD", "AUD", "GBP"] as const;
type FxQuote = (typeof FX_QUOTES)[number];

export function fxFromDate(monthYms: string[]): string {
  const first = monthYms[0];
  if (!first) throw new Error("monthYms cannot be empty");
  return `${first}-01`;
}

type SortedSeries = Record<FxQuote, FxRow[]>;

function indexByQuote(daily: FxRow[]): SortedSeries {
  const out: SortedSeries = { NZD: [], AUD: [], GBP: [] };
  for (const row of daily) {
    if ((FX_QUOTES as readonly string[]).includes(row.quote)) {
      out[row.quote as FxQuote].push(row);
    }
  }
  for (const q of FX_QUOTES) out[q].sort((a, b) => a.date.localeCompare(b.date));
  return out;
}

// Point-in-time view: every month uses the latest available daily rate for each quote.
function buildPivot(daily: FxRow[], monthYms: string[]): Record<FxQuote, number[]> {
  const series = indexByQuote(daily);
  const out: Record<FxQuote, number[]> = { NZD: [], AUD: [], GBP: [] };
  for (const q of FX_QUOTES) {
    const rows = series[q];
    if (rows.length === 0) throw new Error(`Missing FX data for ${q}`);
    const latest = rows[rows.length - 1]!.rate;
    out[q] = monthYms.map(() => latest);
  }
  return out;
}

export type FxShift = {
  latest: number;
  baseline: number;
  pctLocalVsUsd: number;
  baselineDate: string;
  latestDate: string;
};

// For each non-USD quote, compute how the local currency has shifted *against* the USD
// since the close of the prior month. Positive = local currency strengthened (our revenue
// now buys more USD); negative = weakened (buys less USD).
//
// `baselineYm` is the YYYY-MM of the last closed month. The baseline rate is the last
// published Frankfurter rate within that month (~last business day).
export function computeFxShifts(daily: FxRow[], baselineYm: string): Record<FxQuote, FxShift> {
  const series = indexByQuote(daily);
  const out = {} as Record<FxQuote, FxShift>;
  for (const q of FX_QUOTES) {
    const rows = series[q];
    if (rows.length === 0) throw new Error(`Missing FX data for ${q}`);
    const inOrBefore = rows.filter((r) => r.date.slice(0, 7) <= baselineYm);
    const baseline = inOrBefore[inOrBefore.length - 1] ?? rows[0]!;
    const latest = rows[rows.length - 1]!;
    const baselineLocalUsd = 1 / baseline.rate;
    const latestLocalUsd = 1 / latest.rate;
    out[q] = {
      latest: latest.rate,
      baseline: baseline.rate,
      pctLocalVsUsd: (latestLocalUsd - baselineLocalUsd) / baselineLocalUsd,
      baselineDate: baseline.date,
      latestDate: latest.date,
    };
  }
  return out;
}

// Per-month closing FX rates and the prior-month close (used to lock USD targets).
//
// fxUsdClose[r][i]      = local→USD using the last published Frankfurter rate within
//                         month i. For the current month we fall back to the latest
//                         available rate (i.e. today). Used to value MRR per-month at
//                         that month's close.
// fxUsdPriorClose[r][i] = fxUsdClose[r][i-1]; for i=0 it falls back to its own close.
//                         Used to value the EoM USD target so that target is locked
//                         the moment the prior month closes; intra-month FX drift
//                         then shows up as a headwind / tailwind against that target.
export function buildFxMonthCloseMaps(
  daily: FxRow[],
  monthYms: string[],
): { fxUsdClose: FxArrMap; fxUsdPriorClose: FxArrMap } {
  const series = indexByQuote(daily);
  const monthEnd: Record<FxQuote, number[]> = { NZD: [], AUD: [], GBP: [] };
  for (const q of FX_QUOTES) {
    const rows = series[q];
    if (rows.length === 0) throw new Error(`Missing FX data for ${q}`);
    monthEnd[q] = monthYms.map((ym) => {
      const inMonth = rows.filter((row) => row.date.slice(0, 7) === ym);
      if (inMonth.length > 0) return inMonth[inMonth.length - 1]!.rate;
      return rows[rows.length - 1]!.rate;
    });
  }

  const ones = monthYms.map(() => 1);
  const fxUsdClose: FxArrMap = {
    NZ: monthEnd.NZD.map((r) => 1 / r),
    AU: monthEnd.AUD.map((r) => 1 / r),
    UK: monthEnd.GBP.map((r) => 1 / r),
    Other: ones,
  };
  const shiftPrior = (arr: number[]) => arr.map((_, i) => arr[i - 1] ?? arr[0]!);
  const fxUsdPriorClose: FxArrMap = {
    NZ: shiftPrior(fxUsdClose.NZ),
    AU: shiftPrior(fxUsdClose.AU),
    UK: shiftPrior(fxUsdClose.UK),
    Other: ones,
  };
  return { fxUsdClose, fxUsdPriorClose };
}

export function buildFxMaps(
  daily: FxRow[],
  monthYms: string[],
): { fxUsd: FxArrMap; fxNzd: FxArrMap } {
  const pivot = buildPivot(daily, monthYms);
  const ones = monthYms.map(() => 1);

  // Local currency → USD: invert the USD-pivot for non-USD regions, identity for USD.
  const fxUsd: FxArrMap = {
    NZ: pivot.NZD.map((r) => 1 / r),
    AU: pivot.AUD.map((r) => 1 / r),
    UK: pivot.GBP.map((r) => 1 / r),
    Other: ones,
  };

  // Local currency → NZD: route through USD pivot (NZD/quote * 1).
  const fxNzd: FxArrMap = {
    NZ: ones,
    AU: pivot.NZD.map((nzd, i) => nzd / (pivot.AUD[i] as number)),
    UK: pivot.NZD.map((nzd, i) => nzd / (pivot.GBP[i] as number)),
    Other: pivot.NZD.slice(),
  };

  return { fxUsd, fxNzd };
}
